import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X, MessageCircle, AtSign, FileText, Bell, Trash2, Check, Coins } from 'lucide-react';
import { markNotificationAsRead, deleteNotification } from './notificationService';
import { getAllAliases } from './aliasService';
import CopyButton from './CopyButton';
import { buildAddressUrl } from './urlUtils';
import { trackNotificationClick } from './analytics';
import { formatAddress, isEmailUserIdentifier } from './addressUtils';
import { getEmailUserDisplayInfo } from './walletService';

const NotificationDropdown = ({ notifications, onClose, user }) => {
  const [aliases, setAliases] = useState({});
  const [emailUserDisplayNames, setEmailUserDisplayNames] = useState({}); // Cache for email user display names

  // Load aliases when component mounts
  useEffect(() => {
    const loadAliases = async () => {
      if (user) {
        const loadedAliases = await getAllAliases(user.uid);
        setAliases(loadedAliases);
      }
    };
    loadAliases();
  }, [user]);

  // Load email user display names for actors in notifications
  useEffect(() => {
    const loadEmailUserNames = async () => {
      // Collect unique email user IDs from notifications
      const emailUserIds = new Set();
      notifications.forEach(n => {
        if (isEmailUserIdentifier(n.actorAddress)) {
          emailUserIds.add(n.actorAddress);
        }
      });

      if (emailUserIds.size === 0) return;

      // Fetch display info for each email user
      const displayNames = { ...emailUserDisplayNames };
      await Promise.all(
        Array.from(emailUserIds).map(async (uid) => {
          if (!displayNames[uid]) {
            const info = await getEmailUserDisplayInfo(uid);
            if (info?.displayName || info?.username) {
              displayNames[uid] = info.displayName || `@${info.username}`;
            }
          }
        })
      );
      setEmailUserDisplayNames(displayNames);
    };
    loadEmailUserNames();
  }, [notifications]); // eslint-disable-line react-hooks/exhaustive-deps

  // Get display name (alias if available, email user display name, or formatted address)
  const getDisplayName = (address, chain = 'ethereum') => {
    if (!address) return '';

    // For email users, use cached display name
    if (isEmailUserIdentifier(address)) {
      return emailUserDisplayNames[address] || 'User';
    }

    const normalizedAddress = address.toLowerCase();
    // Aliases are now stored with just the normalized address (chain-agnostic)
    return aliases[normalizedAddress] || formatAddress(address);
  };

  const getTimeAgo = (date) => {
    const seconds = Math.floor((new Date() - date) / 1000);

    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return `${Math.floor(seconds / 604800)}w ago`;
  };

  const getNotificationText = (notification) => {
    const isCoinNotification = notification.targetType === 'coin';

    switch (notification.type) {
      case 'reply':
        return (
          <div className="flex items-center gap-1 flex-wrap">
            <span className="font-medium">
              {getDisplayName(notification.actorAddress)}
            </span>
            <span>replied to your comment on</span>
            {isCoinNotification ? (
              <span className="font-medium text-blue-600">
                {notification.targetCoinName || notification.targetCoinId}
              </span>
            ) : (
              <>
                <span className="font-medium">
                  {getDisplayName(notification.targetAddress)}
                </span>
                <CopyButton text={notification.targetAddress} label="address" className="!p-0.5" />
              </>
            )}
          </div>
        );
      case 'mention':
        return (
          <div className="flex items-center gap-1 flex-wrap">
            <span className="font-medium">
              {getDisplayName(notification.actorAddress)}
            </span>
            <span>mentioned you on</span>
            {isCoinNotification ? (
              <span className="font-medium text-blue-600">
                {notification.targetCoinName || notification.targetCoinId}
              </span>
            ) : (
              <>
                <span className="font-medium">
                  {getDisplayName(notification.targetAddress)}
                </span>
                <CopyButton text={notification.targetAddress} label="address" className="!p-0.5" />
              </>
            )}
          </div>
        );
      case 'new_comment':
        return (
          <div className="flex items-center gap-1 flex-wrap">
            <span className="font-medium">
              {getDisplayName(notification.actorAddress)}
            </span>
            <span>commented on</span>
            {isCoinNotification ? (
              <span className="font-medium text-blue-600">
                {notification.targetCoinName || notification.targetCoinId}
                {notification.metadata?.watchedCoin && (
                  <span className="ml-1 text-xs text-blue-600">(Watched)</span>
                )}
              </span>
            ) : (
              <>
                <span className="font-medium">
                  {getDisplayName(notification.targetAddress)}
                  {user && user.address && notification.targetAddress?.toLowerCase() === user.address.toLowerCase() && (
                    <span className="ml-1 text-xs text-gray-500">(You)</span>
                  )}
                  {notification.metadata?.watchedAddress && (
                    <span className="ml-1 text-xs text-blue-600">(Watched)</span>
                  )}
                </span>
                <CopyButton text={notification.targetAddress} label="address" className="!p-0.5" />
              </>
            )}
          </div>
        );
      default:
        return 'New notification';
    }
  };

  const getNotificationIcon = (notification) => {
    // For coin notifications, show coin icon
    if (notification.targetType === 'coin') {
      return <Coins className="w-4 h-4" />;
    }

    switch (notification.type) {
      case 'reply':
        return <MessageCircle className="w-4 h-4" />;
      case 'mention':
        return <AtSign className="w-4 h-4" />;
      case 'new_comment':
        return <FileText className="w-4 h-4" />;
      default:
        return <MessageCircle className="w-4 h-4" />;
    }
  };

  const getNotificationUrl = (notification) => {
    // For coin notifications, link to coin page
    if (notification.targetType === 'coin' && notification.targetCoinId) {
      const url = `/coin/${notification.targetCoinId}`;
      return notification.commentId ? `${url}?highlightComment=${notification.commentId}` : url;
    }

    // For address notifications
    const chain = notification.targetChain || 'ethereum';
    return buildAddressUrl(notification.targetAddress, chain, notification.commentId);
  };

  const handleNotificationClick = async (notification) => {
    try {
      // Track notification click
      trackNotificationClick({ type: notification.type, id: notification.id });

      // Mark as read (services use auth.currentUser.uid internally)
      if (!notification.read && user) {
        await markNotificationAsRead(notification.id, user.uid);
      }

      onClose();
    } catch (error) {
      console.error('Error handling notification click:', error);
    }
  };

  const handleMarkAllRead = async () => {
    if (!user) return;

    try {
      // Get all unread notifications
      const unreadNotifications = notifications.filter(n => !n.read);

      // Mark each as read (services use auth.currentUser.uid internally)
      await Promise.all(
        unreadNotifications.map(notification =>
          markNotificationAsRead(notification.id, user.uid)
        )
      );
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleMarkAsRead = async (e, notificationId) => {
    e.stopPropagation(); // Prevent triggering notification click
    if (!user) return;

    try {
      // Services use auth.currentUser.uid internally
      await markNotificationAsRead(notificationId, user.uid);
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleDelete = async (e, notificationId) => {
    e.stopPropagation(); // Prevent triggering notification click
    if (!user) return;

    try {
      // Services use auth.currentUser.uid internally
      await deleteNotification(notificationId, user.uid);
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div className="max-w-[calc(100vw-16px)] w-96 bg-white border border-gray-200 rounded-lg shadow-lg max-h-[600px] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <h3 className="font-semibold text-gray-900">Notifications</h3>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 transition"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Notifications List */}
      <div className="overflow-y-auto flex-1">
        {notifications.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Bell className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No notifications yet</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {notifications.map((notification) => (
              <div
                key={notification.id}
                className={`w-full p-4 hover:bg-gray-50 transition flex gap-3 ${
                  !notification.read ? 'bg-blue-50' : ''
                }`}
              >
                {/* Icon */}
                <div className={`flex-shrink-0 mt-1 ${
                  !notification.read ? 'text-blue-600' : 'text-gray-400'
                }`}>
                  {getNotificationIcon(notification)}
                </div>

                {/* Content - clickable area */}
                <Link
                  to={getNotificationUrl(notification)}
                  onClick={() => handleNotificationClick(notification)}
                  className="flex-1 min-w-0"
                >
                  {/* Notification text */}
                  <div className="text-sm text-gray-800 mb-1">
                    {getNotificationText(notification)}
                  </div>

                  {/* Comment preview */}
                  {notification.metadata?.commentText && (
                    <p className="text-xs text-gray-600 mb-1 truncate">
                      "{notification.metadata.commentText}"
                    </p>
                  )}

                  {/* Timestamp */}
                  <p className="text-xs text-gray-500">
                    {getTimeAgo(notification.createdAt)}
                  </p>
                </Link>

                {/* Actions */}
                <div className="flex-shrink-0 flex items-center gap-1">
                  {/* Unread indicator */}
                  {!notification.read && (
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  )}

                  {/* Mark as read button - only show for unread notifications */}
                  {!notification.read && (
                    <button
                      onClick={(e) => handleMarkAsRead(e, notification.id)}
                      className="text-gray-400 hover:text-green-600 transition p-1"
                      title="Mark as read"
                    >
                      <Check className="w-4 h-4" />
                    </button>
                  )}

                  {/* Delete button */}
                  <button
                    onClick={(e) => handleDelete(e, notification.id)}
                    className="text-gray-400 hover:text-red-600 transition p-1"
                    title="Delete notification"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer - Mark all as read */}
      {unreadCount > 0 && (
        <div className="border-t border-gray-200 p-3">
          <button
            onClick={handleMarkAllRead}
            className="w-full text-center text-sm text-blue-600 hover:text-blue-700 font-medium transition"
          >
            Mark all as read
          </button>
        </div>
      )}
    </div>
  );
};

export default NotificationDropdown;
